import { create } from 'zustand'
import type { Employee } from '../../screens/EmployeesList/EmployeesList.types'
import type { PreAppointmentPurposeData } from '../../screens/PreAppointmentPurpose/PreAppointmentPurpose.types'
import type { UserInfo } from '../../screens/UserData/UserData.types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppInsights = any

export type ScreenType =
  | 'store-locator'
  | 'employees-list'
  | 'timeslots'
  | 'user-data'
  | 'pre-appointment-purpose'
  | 'confirmation'
  | 'cancel'
  | 'we-chat-confirmation'
  | 'error'

export interface BookingState {
  env: 'tst' | 'acc' | 'prd'
  screen: ScreenType
  /** Screens visited before the current one — drives back-button visibility. */
  screenHistory: ScreenType[]
  /**
   * UUID created once per `BookingToolRoot` mount (`crypto.randomUUID()`).
   * Groups every Avo event of one booking flow — added to every event by
   * `flowProperties()`, never read directly by a screen.
   */
  flowId?: string
  language: string
  location: string
  /**
   * Storefront locale prefix (e.g. `"en-nl"`) for links back into the
   * storefront. Resolved from the host props / entry URL once on seed, and
   * deliberately not recomposed from `language` + `location` at use time: the
   * reschedule flow overwrites `location` with the booked store's country, which
   * would silently retarget the link at a different market than the customer
   * came from.
   */
  locale: string
  /** Country code selected in the store locator — persisted so back-navigation preserves the selection. */
  selectedCountry?: string
  storeId?: string
  storeName?: string
  locationCode?: string
  wwLocationId?: string
  employeeId?: string
  employeeName?: string
  employeeImage?: string
  /** Waitwhile resource ID — needed for booking creation/update. */
  wwResourceId?: string
  serviceId?: string
  bookingId?: string
  /** Selected timeslot ISO datetime, e.g. "2026-08-12T11:30:00". */
  timeslot?: string
  /** Booking duration in seconds — populated from the booking API during reschedule. */
  duration?: number
  /**
   * Drives confirmation UI:
   * - booked / rescheduled — final success with employee + date
   * - appointment-confirmed — brief checkmark screen before purpose
   * - preferences-saved — thank-you after purpose submit
   */
  confirmationVariant?:
    | 'booked'
    | 'rescheduled'
    | 'appointment-confirmed'
    | 'preferences-saved'
  /**
   * Full employee object carried from EmployeesList → Timeslots so the
   * Timeslots screen can skip a redundant API call.  Cleared when the user
   * navigates back to EmployeesList so the list always shows fresh data.
   */
  selectedEmployee?: Employee
  /**
   * The skip flag made the "No preference" choice for the visitor, so the
   * timeslots hide their way back to the advisor list. Cleared as soon as the
   * list is shown instead.
   */
  advisorStepSkipped?: boolean
  /**
   * Customer details entered on the UserData screen. Kept in the store so the
   * form survives back-navigation and a failed create attempt.
   */
  userData?: UserInfo
  /**
   * Prefetched purpose options (`GET appointment-purpose-options`). Loaded on
   * app init when the host did not pass `serviceId`.
   */
  preAppointmentPurpose?: PreAppointmentPurposeData
  /** True when the user skipped the purpose screen without selecting/notes. */
  preferencesSkipped?: boolean
  /**
   * `flowId` of the flow that already sent step 5's `bookingToolStepViewed`.
   * `Confirmation` mounts twice for one `booked` flow — once for
   * `appointment-confirmed`, again for `preferences-saved` (or `booked` when
   * the purpose screen is skipped) — and only the first mount may count as
   * the step view. Keyed on `flowId` rather than a plain boolean so
   * `resetState` (which drops this field along with everything else not in
   * `defaultState`) is enough to let the next flow fire it again.
   */
  confirmationStepViewedFlowId?: string
  /**
   * True once `Cancel`'s auto-cancel succeeds. Alongside `confirmationVariant`
   * (set once a booking/reschedule succeeds and never cleared before the next
   * flow), this is what `BookingToolRoot`'s unmount handler reads to decide
   * whether `bookingToolPanelClosed` belongs to this close — a completed
   * cancel or booking is not a "closed panel", it's the flow finishing.
   */
  cancelConfirmed?: boolean
  userId?: string
  /**
   * Auth token for the customer lookup, from the host prop or `?signature=`.
   * Only ever sent as the `authorization` header — never persisted or logged.
   */
  signature?: string
  /**
   * Host's resolved analytics page type — see
   * `BookingToolRootProps['pageType']`. `syncSystemProperties()` sends `booking_tool`
   * when unset (the standalone app has no host page).
   */
  pageType?: string
  /**
   * True once `userData` came back from the customer lookup rather than the
   * user's own typing. Keeps the prefill from running twice.
   */
  isPrefilled?: boolean
  appInsights?: AppInsights
}

export interface BookingStore extends BookingState {
  setState: (partial: Partial<BookingState>) => void
  /** Navigate forward: pushes the current screen onto history before switching. */
  navigate: (screen: ScreenType) => void
  /** Navigate back: pops the previous screen off history. No-op on the first screen. */
  goBack: () => void
  /**
   * Wipe the flow back to defaults, including every optional field. Host props
   * are not restored here — `BookingToolRoot` re-applies them after the reset.
   */
  resetState: () => void
}

const defaultState: BookingState = {
  env: 'tst',
  screen: 'store-locator',
  screenHistory: [],
  language: 'en',
  location: 'nl',
  locale: 'en-nl',
}

export const useBookingStore = create<BookingStore>((set) => ({
  ...defaultState,
  setState: (partial) => set((state) => ({ ...state, ...partial })),
  navigate: (screen) =>
    set((state) => ({
      ...state,
      screenHistory: [...state.screenHistory, state.screen],
      screen,
    })),
  goBack: () =>
    set((state) => {
      if (state.screenHistory.length === 0) return state
      const history = [...state.screenHistory]
      const previousScreen = history.pop() as ScreenType
      return {
        ...state,
        screen: previousScreen,
        screenHistory: history,
        // Returning to the list must drop the carried employee so Timeslots
        // doesn't reuse stale availability if the user picks again.
        ...(previousScreen === 'employees-list'
          ? { selectedEmployee: undefined }
          : {}),
      }
    }),
  // `replace: true` is load-bearing. `defaultState` only names the six
  // required keys, so a merging `set(defaultState)` would leave userData,
  // timeslot, selectedEmployee, bookingId, confirmationVariant and the rest of
  // the optional fields untouched — the tool would reopen on a clean first
  // screen with the previous booking still behind it. Replacing drops the
  // actions along with the state, so they are carried over explicitly.
  resetState: () =>
    set(
      (state) => ({
        ...defaultState,
        setState: state.setState,
        navigate: state.navigate,
        goBack: state.goBack,
        resetState: state.resetState,
      }),
      true,
    ),
}))

export default useBookingStore
